import React, { useState } from 'react';
import { ConfirmationModal } from '../../molecules/ConfirmationModal';
import './InviteTable.css';

export type InviteStatus = 'pending' | 'active' | 'revoked' | 'rejected';
export type InviteType = 'employee' | 'farmer';

export interface Invite {
  /** Unique invite ID */
  id: string;
  /** Phone number of invitee */
  phone: string;
  /** Type of invite */
  type: InviteType;
  /** Current status */
  status: InviteStatus;
  /** When the invite was created */
  createdAt: string;
  /** When the invite expires (for pending) */
  expiresAt?: string;
}

export interface InviteTableProps {
  /** List of invites to display */
  invites: Invite[];
  /** Handler when revoke is confirmed */
  onRevoke?: (inviteId: string) => void;
  /** Whether a revoke operation is in progress */
  revokeLoading?: boolean;
  /** Optional empty state message */
  emptyMessage?: string;
  /** Optional class name */
  className?: string;
}

const STATUS_LABELS: Record<InviteStatus, string> = {
  pending: 'Pending',
  active: 'Accepted',
  revoked: 'Revoked',
  rejected: 'Rejected',
};

const TYPE_LABELS: Record<InviteType, string> = {
  employee: 'Can manage your farm',
  farmer: 'Separate farm',
};

/**
 * Table displaying user invites with status badges and revoke action.
 * Revoke action is only available for employee-type invites.
 */
export const InviteTable: React.FC<InviteTableProps> = ({
  invites,
  onRevoke,
  revokeLoading = false,
  emptyMessage = 'No active invites',
  className = '',
}) => {
  const [revokeTarget, setRevokeTarget] = useState<Invite | null>(null);

  const handleRevokeClick = (invite: Invite) => {
    setRevokeTarget(invite);
  };

  const handleRevokeConfirm = () => {
    if (revokeTarget && onRevoke) {
      onRevoke(revokeTarget.id);
    }
    setRevokeTarget(null);
  };

  const handleRevokeCancel = () => {
    setRevokeTarget(null);
  };

  if (invites.length === 0) {
    return (
      <div className={`invite-table invite-table--empty ${className}`}>
        <div className="invite-table__empty-state">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={`invite-table ${className}`}>
      <div className="invite-table__container">
        <table className="invite-table__table">
          <tbody>
            {invites.map((invite) => (
              <tr key={invite.id} className="invite-table__row">
                <td className="invite-table__cell invite-table__cell--phone">
                  {invite.phone}
                </td>
                <td className="invite-table__cell invite-table__cell--type">
                  {TYPE_LABELS[invite.type]}
                </td>
                <td className="invite-table__cell invite-table__cell--status">
                  <span className={`invite-table__status invite-table__status--${invite.status}`}>
                    {STATUS_LABELS[invite.status]}
                  </span>
                </td>
                <td className="invite-table__cell invite-table__cell--actions">
                  {invite.type === 'employee' && invite.status !== 'revoked' && invite.status !== 'rejected' && onRevoke && (
                    <button
                      type="button"
                      className="invite-table__revoke-button"
                      onClick={() => handleRevokeClick(invite)}
                      disabled={revokeLoading}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={revokeTarget !== null}
        title="Revoke invite"
        message="Are you sure you want to delete this user from your farm?"
        confirmLabel="Revoke invite"
        cancelLabel="Cancel"
        onConfirm={handleRevokeConfirm}
        onCancel={handleRevokeCancel}
        danger
        loading={revokeLoading}
      />
    </div>
  );
};

export default InviteTable;
